import React, { useState, useEffect, useRef, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { getFullscreenPortalTarget } from './fullscreenPortal';
import {
  FaTools,
  FaTimes,
  FaMapMarkerAlt,
  FaUser,
  FaDesktop,
  FaBan,
  FaVolumeMute,
  FaVolumeUp,
} from 'react-icons/fa';
import API from '../../API';
import IntersectSignEditor from './IntersectSignEditor';
import { sendPlayerVoiceMuteUnmute } from './playerVoiceBridge';
import { useOverlay } from '../../contexts/FocusContext';
import UnityGameDockApp from './UnityGameDockApp';
import type { IVSRealtimeChatRef } from './IVSRealtimeChat';

/** Same source as People widget: GET /events/:eventId/rooms/:roomId/presence */
export interface RoomPresenceUser {
  intersect_user_id: string;
  username: string;
  game_server_instance_id?: string;
  last_seen: number;
}

interface Room {
  room_id: string;
  room_name: string;
  event_id: string;
  chat_room_arn: string;
  stage_arn: string;
  podium_position?: string;
}

const PRESENCE_MAX_AGE_MS = 120_000;
const PRESENCE_POLL_MS = 3000;

type ScreenShareModAction = 'grant' | 'revoke';

interface ModToolsProps {
  accessToken: string;
  eventId: string;
  currentRoomId: string;
  /** IVS chat ref — used to send MOD_TELEPORT (same path as chat moderator actions). */
  chatRef?: RefObject<IVSRealtimeChatRef | null>;
  /** Current room podium string from Unity/API; required for teleport commands. */
  podiumPosition?: string;
  /** False when this room has no chat room (IVS chat not mounted — cannot send MOD_TELEPORT). */
  chatRoomAvailable?: boolean;
  /** Current user intersect id (for “You” label). */
  currentUserId?: string;
  onPodiumTeleport?: () => void;
  onRandomizeAvatar?: () => void;
  isRandomizingAvatar?: boolean;
  dockMode?: boolean;
}

/** Prefer intersect id — matches participant token + IVS chat UserId after ff-api aligns chat token to user id. */
function presenceTeleportTargetId(u: RoomPresenceUser): string {
  const id = u.intersect_user_id?.trim();
  if (id) return id;
  return u.username?.trim() || '';
}

function displayNameForPresence(u: RoomPresenceUser): string {
  const name = u.username?.trim();
  if (name) return name;
  const id = u.intersect_user_id;
  return id ? `${id.slice(0, 8)}…` : 'Unknown';
}

const ModTools: React.FC<ModToolsProps> = ({
  accessToken,
  eventId,
  currentRoomId,
  chatRef,
  podiumPosition = '',
  chatRoomAvailable = true,
  currentUserId,
  onPodiumTeleport,
  onRandomizeAvatar,
  isRandomizingAvatar = false,
  dockMode = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [isLoadingRooms, setIsLoadingRooms] = useState(false);
  const [presenceUsers, setPresenceUsers] = useState<RoomPresenceUser[]>([]);
  const [presenceLoading, setPresenceLoading] = useState(false);
  const [teleportConfirmRoom, setTeleportConfirmRoom] = useState<Room | null>(null);
  const [teleportPresenceUser, setTeleportPresenceUser] = useState<RoomPresenceUser | null>(null);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isSendingTeleport, setIsSendingTeleport] = useState(false);
  const [screenShareModConfirm, setScreenShareModConfirm] = useState<{
    user: RoomPresenceUser;
    action: ScreenShareModAction;
  } | null>(null);
  const [isSendingScreenShareMod, setIsSendingScreenShareMod] = useState(false);
  const presenceRoomKeyRef = useRef<string | null>(null);
  /** Refs don't re-render when chat mounts — tick while Mod is open so Send enables once IVS chat is ready. */
  const [chatReadyTick, setChatReadyTick] = useState(0);
  /** Per-user: true after moderator chose mute from this panel (toggle sends unmute next). */
  const [modVoiceMutedByUserId, setModVoiceMutedByUserId] = useState<Record<string, boolean>>({});

  useOverlay(
    'mod-tools',
    isOpen ||
      teleportConfirmRoom !== null ||
      teleportPresenceUser !== null ||
      screenShareModConfirm !== null
  );

  useEffect(() => {
    if (!isOpen) return;
    const id = window.setInterval(() => setChatReadyTick((t) => t + 1), 800);
    return () => clearInterval(id);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) setModVoiceMutedByUserId({});
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && accessToken && eventId) {
      fetchRooms();
    }
  }, [isOpen, accessToken, eventId]);

  const fetchRooms = async () => {
    setIsLoadingRooms(true);
    try {
      const response = await API.getRooms(accessToken, eventId);
      const data = await response.json();
      setRooms(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    } finally {
      setIsLoadingRooms(false);
    }
  };

  useEffect(() => {
    if (!accessToken || !eventId || !currentRoomId || !isOpen) {
      setPresenceUsers([]);
      setPresenceLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const res = await API.getRoomPresence(accessToken, { eventId, roomId: currentRoomId });
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          const raw = (data.users || []) as RoomPresenceUser[];
          const now = Date.now();
          const users = raw.filter((u) => now - u.last_seen < PRESENCE_MAX_AGE_MS);
          users.sort((a, b) =>
            displayNameForPresence(a).localeCompare(displayNameForPresence(b), undefined, {
              sensitivity: 'base',
            })
          );
          setPresenceUsers(users);
        } else {
          setPresenceUsers([]);
        }
      } catch {
        if (!cancelled) setPresenceUsers([]);
      } finally {
        if (!cancelled) setPresenceLoading(false);
      }
    };

    const roomKey = `${accessToken}:${eventId}:${currentRoomId}`;
    if (presenceRoomKeyRef.current !== roomKey) {
      presenceRoomKeyRef.current = roomKey;
      setPresenceLoading(true);
    }
    load();
    const t = window.setInterval(load, PRESENCE_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [accessToken, eventId, currentRoomId, isOpen]);

  const parsePodiumPosition = (positionStr: string): { x: number; y: number; z: number } | null => {
    try {
      const cleaned = positionStr.replace(/[()]/g, '').trim();
      const parts = cleaned.split(',').map((p) => parseFloat(p.trim()));

      if (parts.length === 3 && parts.every((p) => !isNaN(p))) {
        return { x: parts[0], y: parts[1], z: parts[2] };
      }
      return null;
    } catch (error) {
      console.error('Error parsing podium position:', error);
      return null;
    }
  };

  const handleTeleportClick = (room: Room) => {
    setTeleportConfirmRoom(room);
    setSaveMessage(null);
    setIsOpen(false);
  };

  const handlePickPresenceUser = (u: RoomPresenceUser) => {
    setTeleportPresenceUser(u);
    setSaveMessage(null);
    setIsOpen(false);
  };

  const handleConfirmPresenceTeleport = async () => {
    if (!teleportPresenceUser || !chatRef?.current?.sendModeratorTeleportToUser) {
      setSaveMessage({ type: 'error', text: 'Chat is not ready. Open chat or wait for connection.' });
      setTeleportPresenceUser(null);
      return;
    }

    const targetId = presenceTeleportTargetId(teleportPresenceUser);
    setIsSendingTeleport(true);
    try {
      await chatRef.current.sendModeratorTeleportToUser(targetId);
      setSaveMessage({ type: 'success', text: `Teleport sent to ${displayNameForPresence(teleportPresenceUser)}` });
      setTimeout(() => setSaveMessage(null), 4000);
      // onPodiumTeleport for self is invoked inside IVS chat when the target is the moderator
    } catch (e) {
      console.error('[ModTools] Moderator teleport failed:', e);
      setSaveMessage({
        type: 'error',
        text: e instanceof Error ? e.message : 'Failed to send teleport',
      });
    } finally {
      setIsSendingTeleport(false);
      setTeleportPresenceUser(null);
    }
  };

  const handleCancelPresenceTeleport = () => {
    setTeleportPresenceUser(null);
  };

  const handlePickScreenShareMod = (u: RoomPresenceUser, action: ScreenShareModAction) => {
    setScreenShareModConfirm({ user: u, action });
    setSaveMessage(null);
    setIsOpen(false);
  };

  const handleCancelScreenShareMod = () => {
    setScreenShareModConfirm(null);
  };

  const handleConfirmScreenShareMod = async () => {
    if (!screenShareModConfirm) return;
    const grant = chatRef?.current?.sendModeratorGrantScreenShare;
    const revoke = chatRef?.current?.sendModeratorRevokeScreenShare;
    if (!grant || !revoke) {
      setSaveMessage({ type: 'error', text: 'Chat is not ready. Open chat or wait for connection.' });
      setScreenShareModConfirm(null);
      return;
    }

    const targetId = presenceTeleportTargetId(screenShareModConfirm.user);
    const label = displayNameForPresence(screenShareModConfirm.user);
    setIsSendingScreenShareMod(true);
    try {
      if (screenShareModConfirm.action === 'grant') {
        await grant(targetId);
        setSaveMessage({ type: 'success', text: `Screen share granted to ${label}` });
      } else {
        await revoke(targetId);
        setSaveMessage({ type: 'success', text: `Screen share revoked from ${label}` });
      }
      setTimeout(() => setSaveMessage(null), 4000);
      setScreenShareModConfirm(null);
    } catch (e) {
      console.error('[ModTools] Moderator screen share action failed:', e);
      setSaveMessage({
        type: 'error',
        text: e instanceof Error ? e.message : 'Failed to update screen share permission',
      });
    } finally {
      setIsSendingScreenShareMod(false);
    }
  };

  const handleConfirmTeleport = () => {
    if (!teleportConfirmRoom || !teleportConfirmRoom.podium_position) {
      return;
    }

    const position = parsePodiumPosition(teleportConfirmRoom.podium_position);

    if (!position) {
      setSaveMessage({ type: 'error', text: 'Invalid podium position format' });
      setTeleportConfirmRoom(null);
      return;
    }

    console.log('[ModTools→Unity] Teleporting to podium:', position);

    try {
      if (window.sendMessageToUnity) {
        const teleportData = {
          eventType: 'Teleport',
          payload: JSON.stringify({
            position: position,
            rotation: { x: 0, y: 0, z: 0 },
          }),
        };
        window.sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(teleportData));

        setSaveMessage({ type: 'success', text: 'Teleported to podium!' });
        setTimeout(() => setSaveMessage(null), 3000);

        onPodiumTeleport?.();
      } else {
        setSaveMessage({ type: 'error', text: 'Unity bridge not available' });
      }
    } catch (error) {
      console.error('[ModTools→Unity] Error teleporting:', error);
      setSaveMessage({ type: 'error', text: 'Failed to teleport' });
    }

    setTeleportConfirmRoom(null);
  };

  const handleCancelTeleport = () => {
    setTeleportConfirmRoom(null);
  };

  const handleVoiceMuteToggle = (targetUserId: string, label: string) => {
    const currentlyMutedInPanel = Boolean(modVoiceMutedByUserId[targetUserId]);
    const eventType = currentlyMutedInPanel ? 'PlayerUnmute' : 'PlayerMute';
    if (!sendPlayerVoiceMuteUnmute(eventType, targetUserId, 'ModTools')) {
      setSaveMessage({ type: 'error', text: 'Unity bridge not available' });
      setTimeout(() => setSaveMessage(null), 4000);
      return;
    }
    setModVoiceMutedByUserId((prev) => ({ ...prev, [targetUserId]: !currentlyMutedInPanel }));
    setSaveMessage({
      type: 'success',
      text: currentlyMutedInPanel
        ? `Moderator action: restored voice for ${label}`
        : `Moderator action: force-muted ${label}`,
    });
    setTimeout(() => setSaveMessage(null), 3000);
  };

  const hasPodium = Boolean(podiumPosition && podiumPosition.trim() !== '');
  void chatReadyTick;
  const chatSendReady = Boolean(chatRef?.current?.sendModeratorTeleportToUser);
  const chatScreenshareModReady = Boolean(
    chatRef?.current?.sendModeratorGrantScreenShare &&
      chatRef?.current?.sendModeratorRevokeScreenShare &&
      chatRef.current?.isIntersectStaff?.()
  );
  const canSendModTeleport = hasPodium && chatRoomAvailable && chatSendReady;
  const canModScreenshare = chatRoomAvailable && chatScreenshareModReady;

  const panelBody = (
    <>
      <div className="flex items-center justify-between border-b border-[#333] bg-purple-600 bg-opacity-20 p-4">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-white">
          <FaTools />
          Moderator Tools
        </h3>
        <button
          type="button"
          onClick={() => setIsOpen(false)}
          className="text-white transition-colors hover:text-purple-300"
        >
          <FaTimes size={18} />
        </button>
      </div>
      {saveMessage && (
        <div
          className={`mx-4 mt-4 rounded-lg border p-3 text-sm ${
            saveMessage.type === 'success'
              ? 'border-green-600 bg-green-600 bg-opacity-20 text-green-400'
              : 'border-red-600 bg-red-600 bg-opacity-20 text-red-400'
          }`}
        >
          {saveMessage.text}
        </div>
      )}
      <div className="max-h-[min(60vh,24rem)] flex-1 space-y-4 overflow-y-auto p-4">
        {onRandomizeAvatar && (
          <div>
            <div className="mb-2 text-xs font-semibold uppercase text-[#999]">Your Avatar</div>
            <button
              type="button"
              onClick={onRandomizeAvatar}
              disabled={isRandomizingAvatar}
              className="rounded-lg bg-purple-700 px-4 py-2 text-sm font-medium text-white transition hover:bg-purple-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {isRandomizingAvatar ? 'Changing avatar…' : 'Random Clay Avatar'}
            </button>
          </div>
        )}
        <div>
          <div className="mb-2 text-xs font-semibold uppercase text-[#999]">People in this room</div>
          {!hasPodium && (
            <p className="mb-2 text-xs text-amber-400/90">
              This room has no podium position set — teleport targets cannot be moved to a podium until it is configured.
            </p>
          )}
          {presenceLoading && presenceUsers.length === 0 ? (
            <div className="py-6 text-center text-[#666]">Loading people…</div>
          ) : presenceUsers.length === 0 ? (
            <div className="py-6 text-center text-[#666]">No one listed yet (same list as the People app).</div>
          ) : (
            <ul className="space-y-2">
              {presenceUsers.map((u) => {
                const isYou =
                  Boolean(currentUserId) &&
                  (u.intersect_user_id === currentUserId || u.username === currentUserId);
                const label = displayNameForPresence(u);
                const targetId = presenceTeleportTargetId(u);
                const voiceMutedHere = Boolean(modVoiceMutedByUserId[targetId]);
                return (
                  <li
                    key={u.intersect_user_id || u.username}
                    className="flex items-center justify-between gap-2 rounded-lg border border-[#444] bg-[#2a2a2a] px-3 py-2"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#333] text-[#aaa]">
                        <FaUser size={14} />
                      </span>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-white">
                          {label}
                          {isYou && (
                            <span className="ml-2 text-xs font-normal text-purple-400">(You)</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
                      <button
                        type="button"
                        disabled={isYou}
                        title={
                          isYou
                            ? 'Use other controls — moderators do not force-mute themselves from this list'
                            : voiceMutedHere
                              ? 'Moderator: restore voice for this player (force unmute)'
                              : 'Moderator: force mute this player'
                        }
                        aria-pressed={voiceMutedHere}
                        onClick={() => handleVoiceMuteToggle(targetId, label)}
                        className={`rounded px-2 py-1.5 text-white transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                          voiceMutedHere
                            ? 'bg-amber-800/90 hover:bg-amber-700'
                            : 'bg-gray-700 hover:bg-gray-600'
                        }`}
                      >
                        {voiceMutedHere ? (
                          <FaVolumeUp size={12} className="mx-0.5" aria-hidden />
                        ) : (
                          <FaVolumeMute size={12} className="mx-0.5" aria-hidden />
                        )}
                      </button>
                      <button
                        type="button"
                        disabled={!canModScreenshare || isSendingScreenShareMod}
                        title={
                          !chatRoomAvailable
                            ? 'Chat not available for this room'
                            : !chatRef?.current?.sendModeratorGrantScreenShare
                              ? 'Waiting for chat connection…'
                              : !chatRef?.current?.isIntersectStaff?.()
                                ? 'Moderator only'
                                : 'Grant screen share permission'
                        }
                        onClick={() => handlePickScreenShareMod(u, 'grant')}
                        className="rounded bg-green-700 px-2 py-1.5 text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <FaDesktop size={12} className="mx-0.5" />
                      </button>
                      <button
                        type="button"
                        disabled={!canModScreenshare || isSendingScreenShareMod}
                        title={
                          !chatRoomAvailable
                            ? 'Chat not available for this room'
                            : !chatRef?.current?.sendModeratorRevokeScreenShare
                              ? 'Waiting for chat connection…'
                              : !chatRef?.current?.isIntersectStaff?.()
                                ? 'Moderator only'
                                : 'Revoke screen share permission'
                        }
                        onClick={() => handlePickScreenShareMod(u, 'revoke')}
                        className="rounded bg-red-800 px-2 py-1.5 text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <FaBan size={12} className="mx-0.5" />
                      </button>
                      <button
                        type="button"
                        disabled={!canSendModTeleport || isSendingTeleport}
                        title={
                          !hasPodium
                            ? 'No podium position for this room'
                            : !chatRoomAvailable
                              ? 'Chat not available for this room'
                              : !chatSendReady
                                ? 'Waiting for chat connection…'
                                : 'Teleport this person to the podium'
                        }
                        onClick={() => handlePickPresenceUser(u)}
                        className="rounded bg-blue-600 px-2.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Teleport
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div>
          <div className="mb-2 text-xs font-semibold uppercase text-[#999]">Room Podium Positions</div>
          {isLoadingRooms ? (
            <div className="py-8 text-center text-[#666]">Loading rooms...</div>
          ) : rooms.filter((room) => room.podium_position && room.podium_position.trim() !== '').length ===
            0 ? (
            <div className="py-8 text-center text-[#666]">No rooms with podium positions</div>
          ) : (
            rooms
              .filter((room) => room.podium_position && room.podium_position.trim() !== '')
              .map((room) => {
                const isCurrentRoom = room.room_id === currentRoomId;
                return (
                  <div
                    key={room.room_id}
                    className={`mb-3 rounded-lg border p-3 transition-all duration-200 ${
                      isCurrentRoom
                        ? 'border-purple-600 bg-purple-600 bg-opacity-10'
                        : 'border-[#444] bg-[#2a2a2a]'
                    }`}
                  >
                    <div className="space-y-2">
                      <div className="flex min-w-0 flex-1 items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium text-white">{room.room_name}</div>
                          <div className="mt-1 text-xs text-[#666]">
                            {room.room_id}
                            {isCurrentRoom && <span className="ml-2 text-purple-400">(Current)</span>}
                          </div>
                        </div>
                      </div>
                      {isCurrentRoom && (
                        <button
                          type="button"
                          onClick={() => handleTeleportClick(room)}
                          className="flex w-full items-center justify-center gap-2 rounded bg-blue-600 px-3 py-2 text-sm text-white transition-all duration-200 hover:bg-blue-700"
                        >
                          <FaMapMarkerAlt size={12} />
                          Teleport yourself to podium
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
          )}
        </div>
        <details key={eventId} className="rounded-lg border border-[#444] bg-[#2a2a2a]">
          <summary className="cursor-pointer px-3 py-3 text-sm font-semibold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-purple-400">
            IntersectSign upload
          </summary>
          <div className="px-3 pb-3">
            <IntersectSignEditor accessToken={accessToken} eventId={eventId} />
          </div>
        </details>
      </div>
    </>
  );

  return (
    <>
      {dockMode ? (
        <UnityGameDockApp
          ariaLabel="Moderator tools"
          label="Mod"
          icon={<FaTools />}
          active={isOpen}
          onRequestClose={() => setIsOpen(false)}
          tileClassName="bg-gradient-to-br from-amber-500 to-yellow-700"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
        >
          {isOpen && (
            <div
              className="unity-dock-popover unity-dock-popover--enter unity-dock-popover--wide flex max-h-[min(85vh,32rem)] flex-col border-purple-600/40"
              onClick={(e) => e.stopPropagation()}
            >
              {panelBody}
            </div>
          )}
        </UnityGameDockApp>
      ) : (
        <div className="relative">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
            className={`flex items-center gap-2 rounded-lg px-4 py-2 font-semibold text-white shadow-lg transition-all duration-200 ${
              isOpen ? 'bg-yellow-700 bg-opacity-90' : 'bg-yellow-600 bg-opacity-80 hover:bg-opacity-100'
            }`}
            title="Moderator Tools"
          >
            <FaTools size={16} />
            Moderator
          </button>
          {isOpen && (
            <div
              className="absolute right-0 top-full z-50 mt-2 flex max-h-[70vh] w-96 flex-col rounded-lg border border-purple-600 bg-[#1a1a1a] shadow-2xl"
              style={{ zIndex: 10001 }}
              onClick={(e) => e.stopPropagation()}
            >
              {panelBody}
            </div>
          )}
        </div>
      )}

      {teleportConfirmRoom &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            data-mod-tools-teleport-modal
            className="fixed inset-0 z-[10003] flex items-center justify-center bg-black/50"
            onClick={handleCancelTeleport}
          >
            <div
              className="mx-4 w-full max-w-md rounded-lg border border-purple-600 bg-[#1a1a1a] p-6 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4 flex items-center gap-3">
                <div className="rounded-full bg-blue-600 bg-opacity-20 p-3">
                  <FaMapMarkerAlt className="text-blue-400" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Teleport to Podium?</h3>
                  <p className="mt-1 text-sm text-[#999]">{teleportConfirmRoom.room_name}</p>
                </div>
              </div>

              <div className="mb-6 rounded-lg bg-[#2a2a2a] p-3">
                <div className="mb-1 text-xs text-[#999]">Position:</div>
                <div className="font-mono text-sm text-white">{teleportConfirmRoom.podium_position}</div>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleConfirmTeleport}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 px-6 py-3 font-semibold text-white transition-all duration-200 hover:bg-blue-700"
                >
                  <FaMapMarkerAlt size={14} />
                  Yes, Teleport
                </button>
                <button
                  type="button"
                  onClick={handleCancelTeleport}
                  className="flex-1 rounded-lg bg-gray-600 px-6 py-3 font-semibold text-white transition-all duration-200 hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>,
          getFullscreenPortalTarget()
        )}

      {teleportPresenceUser &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            data-mod-tools-presence-teleport-modal
            className="fixed inset-0 z-[10003] flex items-center justify-center bg-black/50"
            onClick={handleCancelPresenceTeleport}
          >
            <div
              className="mx-4 w-full max-w-md rounded-lg border border-purple-600 bg-[#1a1a1a] p-6 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4 flex items-center gap-3">
                <div className="rounded-full bg-blue-600 bg-opacity-20 p-3">
                  <FaMapMarkerAlt className="text-blue-400" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Teleport to podium?</h3>
                  <p className="mt-1 text-sm text-[#999]">{displayNameForPresence(teleportPresenceUser)}</p>
                </div>
              </div>

              <p className="mb-6 text-sm leading-relaxed text-[#bbb]">
                They will be moved to this room&apos;s podium position. This uses the same chat command as the
                moderator action in chat.
              </p>

              <div className="flex gap-3">
                <button
                  type="button"
                  disabled={isSendingTeleport}
                  onClick={handleConfirmPresenceTeleport}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 px-6 py-3 font-semibold text-white transition-all duration-200 hover:bg-blue-700 disabled:opacity-50"
                >
                  <FaMapMarkerAlt size={14} />
                  {isSendingTeleport ? 'Sending…' : 'Confirm'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelPresenceTeleport}
                  className="flex-1 rounded-lg bg-gray-600 px-6 py-3 font-semibold text-white transition-all duration-200 hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>,
          getFullscreenPortalTarget()
        )}

      {screenShareModConfirm &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            data-mod-tools-screenshare-modal
            className="fixed inset-0 z-[10003] flex items-center justify-center bg-black/50"
            onClick={handleCancelScreenShareMod}
          >
            <div
              className="mx-4 w-full max-w-md rounded-lg border border-purple-600 bg-[#1a1a1a] p-6 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4 flex items-center gap-3">
                <div
                  className={`rounded-full p-3 ${
                    screenShareModConfirm.action === 'grant'
                      ? 'bg-green-600 bg-opacity-20'
                      : 'bg-red-600 bg-opacity-20'
                  }`}
                >
                  {screenShareModConfirm.action === 'grant' ? (
                    <FaDesktop className="text-green-400" size={24} />
                  ) : (
                    <FaBan className="text-red-400" size={24} />
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    {screenShareModConfirm.action === 'grant'
                      ? 'Grant screen share?'
                      : 'Revoke screen share?'}
                  </h3>
                  <p className="mt-1 text-sm text-[#999]">
                    {displayNameForPresence(screenShareModConfirm.user)}
                  </p>
                </div>
              </div>

              <p className="mb-6 text-sm leading-relaxed text-[#bbb]">
                {screenShareModConfirm.action === 'grant'
                  ? 'Same moderator action as in chat: the participant can start presenting from the video panel.'
                  : 'Same moderator action as in chat: their screen share permission will be removed.'}
              </p>

              <div className="flex gap-3">
                <button
                  type="button"
                  disabled={isSendingScreenShareMod}
                  onClick={handleConfirmScreenShareMod}
                  className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-6 py-3 font-semibold text-white transition-all duration-200 disabled:opacity-50 ${
                    screenShareModConfirm.action === 'grant'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {screenShareModConfirm.action === 'grant' ? (
                    <>
                      <FaDesktop size={14} />
                      {isSendingScreenShareMod ? 'Sending…' : 'Grant'}
                    </>
                  ) : (
                    <>
                      <FaBan size={14} />
                      {isSendingScreenShareMod ? 'Sending…' : 'Revoke'}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCancelScreenShareMod}
                  className="flex-1 rounded-lg bg-gray-600 px-6 py-3 font-semibold text-white transition-all duration-200 hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>,
          getFullscreenPortalTarget()
        )}
    </>
  );
};

export default ModTools;
